/**
 * Utilities for setting up Stripe in user projects
 * Injects serverless functions and configuration
 */

import type { JSRuntimeFS } from '@/lib/JSRuntime';
import type { StripeIntegration } from '@/contexts/IntegrationsContext';
import { getServerlessTemplate, getServerlessFunctionPath, stripeDependencies } from './stripe-templates';

export interface StripeProjectSetupOptions {
  /** The project directory path */
  projectDir: string;
  /** The Stripe integration to use */
  integration: StripeIntegration;
  /** Deployment platform */
  platform: 'netlify' | 'vercel';
  /** Which functions to include */
  functions?: Array<'payment-intent' | 'checkout-session' | 'subscription' | 'webhook'>;
}

/**
 * Sets up Stripe integration in a project
 * Creates serverless functions and adds necessary dependencies
 */
export async function setupStripeInProject(
  fs: JSRuntimeFS,
  options: StripeProjectSetupOptions
): Promise<void> {
  const { projectDir, integration, platform, functions = ['payment-intent', 'checkout-session'] } = options;

  // Read package.json
  const packageJsonPath = `${projectDir}/package.json`;
  let packageJson: any;
  
  try {
    const content = await fs.readFile(packageJsonPath, 'utf8');
    packageJson = JSON.parse(content);
  } catch (error) {
    throw new Error('package.json not found in project directory');
  }

  // Add Stripe dependencies
  packageJson.dependencies = {
    ...packageJson.dependencies,
    ...stripeDependencies,
  };

  // Write updated package.json
  await fs.writeFile(packageJsonPath, JSON.stringify(packageJson, null, 2), 'utf8');

  // Create serverless functions directory
  const functionsBaseDir = platform === 'netlify' ? `${projectDir}/netlify/functions` : `${projectDir}/api`;
  
  try {
    await fs.stat(functionsBaseDir);
  } catch {
    await fs.mkdir(functionsBaseDir, { recursive: true });
  }

  // Create each serverless function
  for (const functionType of functions) {
    const functionPath = `${projectDir}/${getServerlessFunctionPath(platform, functionType)}`;
    const template = getServerlessTemplate(platform, functionType);
    
    // Ensure parent directory exists
    const dirPath = functionPath.substring(0, functionPath.lastIndexOf('/'));
    try {
      await fs.stat(dirPath);
    } catch {
      await fs.mkdir(dirPath, { recursive: true });
    }

    await fs.writeFile(functionPath, template, 'utf8');
  }

  // Create environment variables template
  const envTemplate = generateEnvTemplate(integration, platform);
  const envPath = `${projectDir}/.env.example`;
  
  await fs.writeFile(envPath, envTemplate, 'utf8');

  // Create README with setup instructions
  const readmePath = `${projectDir}/STRIPE_SETUP.md`;
  const readme = generateSetupReadme(integration, platform, functions);
  
  await fs.writeFile(readmePath, readme, 'utf8');
}

/**
 * Generates environment variables template
 */
function generateEnvTemplate(integration: StripeIntegration, platform: string): string {
  return `# Stripe Configuration
# Copy this file to .env and fill in your Stripe keys
# IMPORTANT: Never commit .env to version control!

# Your Stripe publishable key (safe for client-side use)
VITE_STRIPE_PUBLISHABLE_KEY=${integration.publishableKey}

# Your Stripe secret key (KEEP THIS SECRET!)
# Get this from: https://dashboard.stripe.com/apikeys
STRIPE_SECRET_KEY=sk_${integration.livemode ? 'live' : 'test'}_your_secret_key_here

# Webhook signing secret (for webhook verification)
# Get this from: https://dashboard.stripe.com/webhooks
STRIPE_WEBHOOK_SECRET=whsec_your_webhook_secret_here
`;
}

/**
 * Generates setup instructions README
 */
function generateSetupReadme(
  integration: StripeIntegration,
  platform: string,
  functions: string[]
): string {
  const platformName = platform === 'netlify' ? 'Netlify' : 'Vercel';
  
  return `# Stripe Integration Setup

This project has been configured with Stripe payment processing using ${platformName} serverless functions.

## Quick Start

### 1. Get Your Stripe Keys

Visit your [Stripe Dashboard](https://dashboard.stripe.com/apikeys) and copy:

- **Publishable key**: Already configured as \`${integration.publishableKey}\`
- **Secret key**: Copy this and keep it secure

### 2. Configure Environment Variables

#### For ${platformName} Deployment:

1. Go to your ${platformName} dashboard
2. Navigate to your site settings
3. Add these environment variables:

\`\`\`
VITE_STRIPE_PUBLISHABLE_KEY=${integration.publishableKey}
STRIPE_SECRET_KEY=sk_${integration.livemode ? 'live' : 'test'}_your_secret_key_here
STRIPE_WEBHOOK_SECRET=whsec_your_webhook_secret_here
\`\`\`

#### For Local Development:

1. Copy \`.env.example\` to \`.env\`
2. Fill in your Stripe secret key
3. Install dependencies: \`npm install\`
4. Run development server: \`npm run dev\`

### 3. Test Your Integration

The following serverless functions are available:

${functions.map(f => `- **${f}**: \`/.netlify/functions/${f}\` or \`/api/${f}\``).join('\n')}

## Available Functions

${generateFunctionDocs(functions)}

## Security Notes

⚠️ **IMPORTANT**: 
- Never commit your \`.env\` file to version control
- Keep your secret key and webhook secret secure
- Use test mode keys for development
- Only use live mode keys in production

## Webhook Setup

To receive Stripe events (like successful payments):

1. Go to [Stripe Webhooks](https://dashboard.stripe.com/webhooks)
2. Click "Add endpoint"
3. Enter your webhook URL: \`https://yourdomain.com/.netlify/functions/webhook\`
4. Select events to listen for
5. Copy the webhook signing secret
6. Add it to your environment variables as \`STRIPE_WEBHOOK_SECRET\`

## Resources

- [Stripe Documentation](https://stripe.com/docs)
- [${platformName} Functions](https://${platform}.com/docs/functions)
- [Stripe Testing](https://stripe.com/docs/testing)

## Support

For issues with Stripe integration, check:
- Stripe Dashboard logs
- ${platformName} function logs
- Browser console for client-side errors
`;
}

/**
 * Generates documentation for each function type
 */
function generateFunctionDocs(functions: string[]): string {
  const docs: Record<string, string> = {
    'payment-intent': `
### Create Payment Intent

Creates a payment intent for card payments.

**Request:**
\`\`\`json
POST /.netlify/functions/payment-intent
{
  "amount": 2999,
  "currency": "usd",
  "metadata": { "orderId": "123" }
}
\`\`\`

**Response:**
\`\`\`json
{
  "clientSecret": "pi_xxx_secret_xxx",
  "id": "pi_xxx"
}
\`\`\`
`,
    'checkout-session': `
### Create Checkout Session

Creates a Stripe Checkout session.

**Request:**
\`\`\`json
POST /.netlify/functions/checkout-session
{
  "priceId": "price_xxx",
  "quantity": 1,
  "successUrl": "https://yourdomain.com/success",
  "cancelUrl": "https://yourdomain.com/cancel"
}
\`\`\`

**Response:**
\`\`\`json
{
  "url": "https://checkout.stripe.com/...",
  "id": "cs_xxx"
}
\`\`\`
`,
    'subscription': `
### Create Subscription

Creates a recurring subscription.

**Request:**
\`\`\`json
POST /.netlify/functions/subscription
{
  "priceId": "price_xxx",
  "customerId": "cus_xxx",
  "metadata": { "plan": "premium" }
}
\`\`\`

**Response:**
\`\`\`json
{
  "subscriptionId": "sub_xxx",
  "clientSecret": "seti_xxx_secret_xxx"
}
\`\`\`
`,
    'webhook': `
### Webhook Handler

Handles Stripe webhook events.

Configure this endpoint in your Stripe Dashboard:
\`https://yourdomain.com/.netlify/functions/webhook\`

Supported events:
- \`payment_intent.succeeded\`
- \`payment_intent.payment_failed\`
- \`checkout.session.completed\`
- \`customer.subscription.created\`
- \`customer.subscription.updated\`
- \`customer.subscription.deleted\`
`,
  };

  return functions.map(f => docs[f] || '').join('\n');
}

/**
 * Gets the deployment environment variables for a Stripe integration
 */
export async function getStripeDeploymentEnvVars(
  integration: StripeIntegration,
  secretKey: string
): Promise<Record<string, string>> {
  return {
    VITE_STRIPE_PUBLISHABLE_KEY: integration.publishableKey,
    STRIPE_SECRET_KEY: secretKey,
    // Webhook secret would need to be configured separately by the user
  };
}
